import { formatCount, joinMeta } from './counts';
import { formatDisplayTime, formatShortDate, formatShortDateTime } from './dateDisplay';
import { formatRelativeToNowCompact } from './formatters';

/**
 * Every sentence the notification list says about a row, GENERATED from the
 * row — never hand-typed on a surface. A notification whose trigger, audience
 * or ladder changes cannot then leave a stale sentence behind it in a list
 * somebody is reading, which is the whole reason these live here rather than
 * inside the components that print them.
 *
 * The API writes the hard part: `trigger.summary` ("When a shift ends and no
 * Administration was logged for a resident that day.") and `audience.summary`
 * ("On-shift staff") come down the wire already in plain words, because only
 * the server knows the rule. What this module adds is the platform's own
 * grammar around them — counts as words through `formatCount`, facts joined
 * with `joinMeta`'s middle dot, nothing at zero.
 *
 * ── The emphasis convention ──────────────────────────────────────────────
 *
 * A generated sentence MAY mark the names inside it — an element, a card, a
 * value — with **single asterisks**:
 *
 *     When a shift ends and no *Administration* was logged for *Suzy Alder*.
 *
 * `splitTerms` reads exactly that and nothing else: one `*`, one or more
 * characters that are not `*`, one `*`. The marked run is a TERM and the
 * surfaces print it in `--ink-mid`/500 (`.notification-row__term`,
 * `.notification-editor__term`) so a name reads in ink inside a muted
 * sentence; everything else is plain prose. There is no nesting, no escaping,
 * no second marker, and no other markdown — this is the lightest possible
 * markup over a field the wire contract freezes as a plain string, not a
 * markdown renderer. An unpaired `*` matches nothing and is printed as typed.
 *
 * **The author of the marks is whoever wrote the sentence**, which is two
 * different parties:
 *
 *   - Sentences this client generates (the editor's read-backs —
 *     `notificationEditorModel.describeTriggerDraft` / `describeResolveDraft`)
 *     already emit them, so the editor's slots read as a marked-up sentence.
 *   - Sentences the SERVER generates — `trigger.summary`, `audience.summary`,
 *     `waitingFor.summary`, and anything else a describer writes — are the
 *     API's to mark. ⚠ **It now does**: `keepr-api utils/notifications/
 *     describe.js` wraps every element and card name it prints (`term()`), and
 *     strips the marks off anything a person typed (`plain()`) so a display
 *     name nobody vetted cannot re-pair them. So a surface that prints one of
 *     those strings VERBATIM shows a reader literal asterisks — that is a bug
 *     on the surface, not a degradation. Every one of them goes through
 *     `splitTerms`, and `components/notifications/TermText` is the one render
 *     half they share.
 *
 * Nothing is ever dropped. A summary with no asterisks in it renders as one
 * plain segment carrying the whole string, so the feature cannot cost a
 * reader a word it did not understand.
 */

/**
 * The trigger sentence, split into the plain prose and the NAMES inside it
 * (an element, a card, a collection), so the row can print a name in ink
 * while the sentence around it stays muted.
 *
 * The convention is `*Administration*` — single asterisks around a name, the
 * lightest possible markup over a field the wire contract freezes as a plain
 * string. A summary that uses none renders as one plain segment, so nothing
 * depends on the API adopting it.
 */
export function splitTerms(text) {
	const source = typeof text === 'string' ? text : '';
	if (!source) return [];
	return source
		.split(/\*([^*]+)\*/g)
		// The capture group lands on every odd index of a split() result.
		.map((part, index) => ({ text: part, term: index % 2 === 1 }))
		.filter(part => part.text !== '');
}

/** What fires this notification, in the API's own words. Null when it says nothing. */
export function describeTrigger(row) {
	const summary = (row?.trigger?.summary || '').trim();
	return summary || null;
}

/**
 * Who it reaches: the audience's own words, then how many people that is
 * right now — "On-shift staff · 3 people". The count is a fact about the
 * audience, so it joins with the middle dot rather than hiding in a
 * parenthesis, and zero renders nothing at all.
 */
export function describeAudience(row) {
	const summary = (row?.audience?.summary || '').trim();
	return joinMeta(summary || null, formatCount(row?.audience?.count, 'person', 'people')) || null;
}

/**
 * "Inbox only" — the honest reading of every channel off. It is NOT a
 * warning: the inbox row is written unconditionally (Decision 11), so the
 * notification still arrives, it just does not leave the app.
 */
export function describeDelivery(row) {
	const channels = row?.channels || {};
	const keys = Object.keys(channels);
	if (!keys.length) return null;
	return keys.every(key => channels[key]?.value !== true) ? 'Inbox only' : null;
}

/**
 * A muted row says so, and says the one thing a reader will wonder about:
 * muting does not stop the notification, it stops the delivery out of the app.
 */
export function describeMute(row) {
	if (!row?.muted) return null;
	const until = row.mutedUntil ? formatShortDateTime(row.mutedUntil) : '';
	return until ? `Muted until ${until}` : 'Muted';
}

/** The tag beside the name when a ladder follows the first message. */
export function describeEscalation(row) {
	const steps = Number(row?.escalation?.steps);
	return Number.isFinite(steps) && steps > 1 ? `Escalates ×${steps}` : null;
}

// Most specific wins, exactly as the settings cascade resolves:
// definition defaults -> global -> collection -> user.
const SOURCE_RANK = { default: 0, global: 1, collection: 2, user: 3 };

// What each source is CALLED when you are standing somewhere else and the
// value fell through to you from it, and what it is called when it is yours.
// The two vocabularies never share a word, so the chip is readable without
// its colour — colour is never the only signal.
const INHERITED_WORDS = { user: 'You', collection: 'Collection', global: 'Platform', default: 'Default' };
const OWN_WORDS = { user: 'Mine', collection: 'This collection', global: 'Platform' };

// The same fact as a SENTENCE, for the two places the word is spoken rather
// than read in its column: the phone fold (line three of the row) and the
// visually-hidden preamble every `SourceMark` carries. A column word and a
// sentence are not interchangeable — "Set by Mine" and "Set by Required" are
// what came of pasting one into the other — so the sentence is written here,
// beside the words it stands in for, and never assembled at a surface.
const OWN_PHRASES = { user: 'Set by you', collection: 'Set by this collection', global: 'Set by the platform' };

/**
 * Where this row's answer came from, as one word for the Set by column.
 *
 * A row has three channels and therefore up to three provenances; the mark
 * reports the MOST SPECIFIC of them, because that is the one a reader is
 * being asked about ("did I decide this, or did someone above me?"). When
 * that is the tier you are standing on the mark is yours — an action-tint
 * chip — and otherwise it names the tier it fell through from, muted.
 *
 * A channel a tier above has FROZEN outranks all of it: the row reads
 * **Required**, with the lock glyph, because nothing here is yours to change.
 *
 * `word` is the column. `phrase` is the same answer said out loud — what the
 * phone fold prints and what a screen reader hears — because the column's
 * vocabulary is not a sentence: "Mine" and "Required" are fine under a *Set by*
 * caption and nonsense after one. An inherited row keeps its documented
 * reading ("Set by Collection"): there the word already IS the tier's name.
 */
export function describeSource(row, tier) {
	const channels = row?.channels || {};
	const locked = Object.keys(channels).some(key => channels[key]?.locked);

	let best = 'default';
	Object.keys(channels).forEach(key => {
		const source = channels[key]?.source;
		if (SOURCE_RANK[source] === undefined) return;
		if (SOURCE_RANK[source] > SOURCE_RANK[best]) best = source;
	});

	// A frozen row says one word either way: "Set by Required" is not a
	// sentence, and the row already carries the Required tag beside its name.
	if (locked) return { word: 'Required', phrase: 'Required', source: best, own: false, locked: true };
	const own = best === tier;
	const word = own ? (OWN_WORDS[tier] || 'Mine') : (INHERITED_WORDS[best] || 'Default');
	return {
		word,
		phrase: own ? (OWN_PHRASES[tier] || 'Set by you') : `Set by ${word}`,
		source: best,
		own,
		locked: false,
	};
}

/** True when this tier decided any part of the row — the only thing a reset has to undo. */
export function hasOwnSettings(row, tier) {
	const channels = row?.channels || {};
	if (Object.keys(channels).some(key => channels[key]?.source === tier)) return true;
	// A mute is this tier's own row too — it is stored in the same overlay —
	// but only when THIS tier is the one that muted it, which `source` never
	// answers: that map is per channel and says nothing about the mute. A bare
	// `muted` offered an enabled reset on a row a tier ABOVE had muted, and
	// that reset then reported success having removed nothing.
	return row?.mutedSource === tier;
}

/**
 * What a reset falls back TO, named for the menu label ("Reset to Platform").
 *
 * This is the next tier DOWN the cascade, which is structural and therefore
 * always the same word on the same surface. It is not a promise that the tier
 * named has an opinion — if it is silent the value keeps falling, to Default —
 * but it is the honest answer to "where does this go when I let go of it?",
 * and an account row belonging to no collection skips the collection tier
 * because there is none to fall to.
 */
export function describeResetTarget(tier, { hasCollection = false } = {}) {
	if (tier === 'collection') return 'Platform';
	if (tier === 'global') return 'Default';
	return hasCollection ? 'Collection' : 'Platform';
}

/* ── The inbox: what an alert is waiting for, and where it has got to ────── */

/**
 * An alert's state, from whichever field carries it.
 *
 * ⚠ **Every inbox row written before this feature has no state at all.** The
 * `notify` action wrote a title, a body and CTAs and nothing else, and those
 * rows are still in the database. The honest reading of a missing state is
 * therefore "not resolved" — the same `{state: {$ne: 'resolved'}}` semantics
 * the API's Open query runs — because a row nobody ever closed is not a row
 * somebody finished. Read `alertStatus` first (the alert's own word) and
 * `state` second (the row's copy of it), and null when neither is there.
 */
export function alertState(row) {
	return row?.alertStatus || row?.state || null;
}

/** Resolved: someone met the obligation, or the criterion was satisfied. */
export function isResolvedAlert(row) {
	return alertState(row) === 'resolved';
}

/**
 * Still in the Open tab. A legacy row falls through to TRUE here, which is
 * the whole point: the tab must not swallow the inbox everyone already has.
 */
export function isUnresolvedAlert(row) {
	return !isResolvedAlert(row);
}

/**
 * Awaiting something FROM YOU — the "Needs attention" group at the top of the
 * list. Strictly `open`, so a legacy row (no state) sits in the chronological
 * list where it always has: it asks for nothing, and a pinned group of rows
 * that ask for nothing is a group that trains people to ignore it.
 */
export function needsAttention(row) {
	return alertState(row) === 'open';
}

/**
 * What the alert is waiting for, in the API's own summary, with the tense the
 * row is actually in. An obligation that has been met reads "Met", not
 * "Waiting for": the sentence is the same fact, and leaving it in the future
 * tense after someone acted is the row lying about what happened.
 */
export function describeWaitingFor(row) {
	const summary = (row?.waitingFor?.summary || '').trim();
	if (!summary) return null;
	return { label: isResolvedAlert(row) ? 'Met' : 'Waiting for', summary, met: isResolvedAlert(row) };
}

/**
 * Where the ladder has got to: the next rung and who it reaches, or the rung
 * it stands on. `escalation.audience` (the step it is ON) is optional and
 * `nextAudience` (the step it is going TO) is the one the API promises, so
 * the sentence degrades to a bare "Escalated · step 2 of 4" rather than
 * naming the wrong group.
 */
export function describeEscalationState(row, { now } = {}) {
	const ladder = row?.escalation || {};
	const step = Number(ladder.step);
	const total = Number(ladder.total);
	// Step 0 is the first message, which nobody calls an escalation.
	const stepLine = Number.isFinite(step) && step > 0 && Number.isFinite(total) && total > 0
		? `step ${step} of ${total}`
		: null;

	if (ladder.nextAt) {
		const when = formatRelativeToNowCompact(ladder.nextAt, now);
		if (when) {
			const audience = (ladder.nextAudience || '').trim();
			return joinMeta(`Escalates ${when}${audience ? ` to ${audience}` : ''}`, stepLine);
		}
	}

	if (stepLine) {
		const audience = (ladder.audience || ladder.nextAudience || '').trim();
		return joinMeta(audience ? `Escalated to ${audience}` : 'Escalated', stepLine);
	}

	return null;
}

// The words for a state that is neither open nor resolved. Each one says what
// happened rather than naming the enum member: "expired" is a database word,
// "nobody answered in time" is what the reader lived through.
const TERMINAL_WORDS = {
	expired: 'Closed — nobody answered in time',
	cancelled: 'Cancelled',
	stalled: 'Stalled — nobody eligible was left to notify',
};

/**
 * The one state line under the waiting-for band, in the order a reader cares
 * about: what a person DID, then how it ended, then where the ladder is.
 *
 * `currentUserId` is what turns "Marcus said I'll take it" into "You said
 * I'll take it". Attribution is never dropped (plan § Sharp edges: one
 * person's snooze silences everyone, so a bare "Snoozed" would be a lie about
 * whose decision it was) — without a name to print it says "Answered", which
 * claims nothing.
 */
export function describeAlertState(row, { now, currentUserId } = {}) {
	const responded = row?.respondedBy;
	const ladder = row?.escalation || {};
	const hasLadder = Number(ladder.total) > 1;

	if (responded) {
		const label = responseLabel(row, responded.responseKey);
		const who = currentUserId && String(responded.userId) === String(currentUserId)
			? 'You'
			: (responded.name || '').trim();
		const said = label
			? (who ? `${who} said ${label}` : `Answered: ${label}`)
			: (who ? `${who} answered` : 'Answered');
		return joinMeta(
			said,
			responded.at ? formatDisplayTime(responded.at) : null,
			hasLadder ? 'escalation stopped' : null
		);
	}

	if (isResolvedAlert(row)) {
		const summary = (row?.resolution?.summary || '').trim();
		const at = row?.resolution?.at || row?.resolvedAt;
		return joinMeta(
			summary ? `Closed — ${summary}` : 'Closed',
			// The date only when the summary did not already carry one.
			summary ? null : (at ? formatShortDate(at) : null),
			// Nobody had to: the row offered buttons and the world answered for it.
			(row?.responses || []).length ? 'nobody had to answer' : null
		);
	}

	const state = alertState(row);
	// A cancelled obligation says WHY when the server said (KPR-874): "the
	// entry it was about no longer matches the reminder's "Only entries
	// where"" is a different fact from "the record was deleted".
	const why = state === 'cancelled' ? (row?.resolution?.summary || '').trim() : '';
	if (why) return `Cancelled — ${why}`;
	const terminal = TERMINAL_WORDS[state];
	if (terminal) return terminal;

	return describeEscalationState(row, { now });
}

/** One response's own label, for the sentence that reports it back. */
export function responseLabel(row, responseKey) {
	if (!responseKey) return null;
	const match = (row?.responses || []).find(response => response?.key === responseKey);
	return (match?.label || '').trim() || null;
}
